import React, { CSSProperties, useEffect, useRef } from "react";
import { Button, LightIndicator, LightStatus } from "@commonsku/styles";
import { useSplitShip } from "./useSplitShip";
import { useDispatch, useSelector } from "react-redux";
import { getStatus, selectSplitShip, splitShipIsLoading } from "../../redux/splitShip";
import styled from "styled-components";
import Status from "../../constants/Status";

const StyledButtonWrapper = styled.div`
  &&& {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }
`;

const SetupSplitShipButton = ({ order_id, onClick, style, disabled }: { order_id: string, onClick: any, style: CSSProperties, disabled: boolean }) => {
  const { accessible, retrieveSplitShip, createSplitShip } = useSplitShip(order_id);
  const splitShip = useSelector(selectSplitShip);
  const loading = useSelector(splitShipIsLoading);
  const dispatch = useDispatch();
  const THIRTY_SECONDS = 30000;
  const intervalIdRef = useRef(null);

  const { split_ship_id, status_id } = splitShip || {};

  const createAndRetrieveSplitShip = (onClick) => {
    createSplitShip();
    onClick();
  };

  useEffect(() => {
      if (accessible && !!order_id) {
          retrieveSplitShip();
      }
  }, [accessible, order_id, retrieveSplitShip]);
  useEffect(() => {
    if (!intervalIdRef.current && [Status.SPLIT_SHIP_PROCESSING_CSV, Status.SPLIT_SHIP_CALCULATING_TAX].includes(status_id)) {
      intervalIdRef.current = setInterval(async () => {
        const response = await dispatch(getStatus({ splitShipId: split_ship_id })) as any;
        const newStatusId = response.payload;
        if ([Status.SPLIT_SHIP_CSV_ERROR, Status.SPLIT_SHIP_READY_FOR_TAX, Status.SPLIT_SHIP_TAX_ERROR].includes(newStatusId)) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        } else if (newStatusId === Status.SPLIT_SHIP_COMPLETED) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
          retrieveSplitShip();
        }
      }, THIRTY_SECONDS);
    }

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [dispatch, status_id, split_ship_id, retrieveSplitShip]);

  const lightIndicatorProps = (): { name: string; status: LightStatus, on?: boolean } => {
    const status = splitShip?.status_id;
    if (!!status && !loading) {
        switch (status) {
            case Status.SPLIT_SHIP_READY_FOR_CSV:
                return { name: "Ready for Spread Sheet", status: 'warning', on: true };
            case Status.SPLIT_SHIP_PROCESSING_CSV:
                return { name: "Validating Spread Sheet…", status: 'loading', on: true };
            case Status.SPLIT_SHIP_READY_FOR_TAX:
                return { name: "Ready to Calculate", status: 'warning', on: true };
            case Status.SPLIT_SHIP_CALCULATING_TAX:
                return { name: "Calculating Taxes…", status: 'loading', on: true };
            case Status.SPLIT_SHIP_CSV_ERROR:
            case Status.SPLIT_SHIP_TAX_ERROR:
                return { name: "Contains Error", status: 'error' };
            case Status.SPLIT_SHIP_COMPLETED:
                return { name: "Setup Complete", status: 'on' };
        }
    }
    // Status.SPLIT_SHIP_OPEN or unknown status
    return { name: "Incomplete Configuration", status: 'off' };
  };
  return accessible ? (
    <StyledButtonWrapper style={style}>
      {splitShip?.active &&
        <LightIndicator large {...lightIndicatorProps()} lightStyles={{ flex: '0 0 auto' }} style={{ marginBottom: 0 }} />
      }
      <Button
        size="small"
        onClick={() => !disabled && createAndRetrieveSplitShip(onClick)}
        data-id="splitShip-setupSplitShip-button"
        disabled={disabled}
      >
        Setup Split Ship
      </Button>
    </StyledButtonWrapper>
  ) : null;
};

export default SetupSplitShipButton;
